// src/pages/Home.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../store/session";
import { useI18n } from "../i18n";
import { getQuestHome, type QuestHomeSummary, type QuestRun } from "../api/endpoints";

function QuickCard({
  to,
  title,
  body,
  badge,
}: {
  to: string;
  title: string;
  body: string;
  badge?: string;
}) {
  return (
    <Link
      to={to}
      className="group rounded-3xl border border-slate-800 bg-slate-950/70 p-5 shadow-[0_10px_30px_rgba(0,0,0,0.25)] transition hover:border-pink-400/40 hover:bg-slate-900/80"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-slate-50 group-hover:text-white">
            {title}
          </h2>
          <p className="mt-2 text-sm leading-6 text-slate-300">
            {body}
          </p>
        </div>
        {!!badge && (
          <span className="rounded-full border border-pink-400/30 bg-pink-500/10 px-3 py-1 text-[11px] font-semibold text-pink-200">
            {badge}
          </span>
        )}
      </div>
    </Link>
  );
}

function runQuestId(run?: QuestRun | null): string {
  return String(run?.questId || run?.quest_id || run?.quest?.id || run?.quest?.slug || "").trim();
}

function runId(run?: QuestRun | null): string {
  return String(run?.id || "").trim();
}

function questId(q?: any): string {
  return String(q?.id || q?.slug || q?.publicId || "").trim();
}

function questTitle(q?: any): string {
  return String(q?.title || q?.name || "Untitled quest");
}

function objectiveTitle(obj?: any): string {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return String(obj?.title || obj?.label || obj?.name || "Daily objective");
}

function questModes(q?: any): string[] {
  return Array.isArray(q?.modes)
    ? q.modes.map((value: any) => String(value || "").trim().toLowerCase()).filter(Boolean)
    : [];
}

function isPartnerLinked(partner?: any): boolean {
  return Boolean(partner?.linked);
}

function questSummary(q?: any, partnerLinked = false): string {
  const meta = q?.meta && typeof q.meta === "object" ? q.meta : {};
  const explicit = q?.summary || meta?.summary || q?.excerpt || q?.body;

  if (explicit) return String(explicit);

  const modes = questModes(q);
  if (!partnerLinked && modes.includes("solo")) {
    return "Start with interests, channels, and friends.";
  }
  if (partnerLinked || modes.includes("partner")) {
    return "Choose something to do together.";
  }

  return "Pick something that fits your mood.";
}

function objectiveBody(
  obj?: any,
  activeRun?: QuestRun | null,
  pendingProposal?: QuestRun | null
): string {
  if (!obj || typeof obj === "string") return "";
  const explicit = obj?.body || obj?.summary || obj?.description;
  if (explicit) return String(explicit);

  if (activeRun?.id) return "Jump back into your active quest.";
  if (pendingProposal?.id) return "Review the proposal and choose your response.";

  const kind = String(obj?.kind || "").trim();
  if (kind === "start_recommended_quest") {
    return "Open today’s recommended quest when you are ready.";
  }
  if (kind === "awaiting_partner") {
    return "Your proposal is waiting for your partner.";
  }

  return "A small prompt for today will appear here when available.";
}

function objectiveLink(
  obj?: any,
  activeRun?: QuestRun | null,
  pendingProposal?: QuestRun | null
): string {
  if (activeRun?.id) return runLink(activeRun);
  if (pendingProposal?.id) return runLink(pendingProposal);

  const qid = String(obj?.questId || obj?.quest_id || "").trim();
  const rid = String(obj?.runId || obj?.run_id || "").trim();

  if (!qid) return "/quests";
  return `/quests/${encodeURIComponent(qid)}${rid ? `?runId=${encodeURIComponent(rid)}` : ""}`;
}

function discoverySettingsBody(partnerLinked: boolean): string {
  return partnerLinked
    ? "Partner-linked ideas are active. Interest, channel, and solo suggestions stay available when you want them."
    : "Interest and channel suggestions appear first. Partner-linked ideas will appear here after you connect with a partner.";
}

function runLink(run?: QuestRun | null): string {
  const qid = runQuestId(run);
  const rid = runId(run);
  if (!qid) return "/quests";
  return `/quests/${encodeURIComponent(qid)}${rid ? `?runId=${encodeURIComponent(rid)}` : ""}`;
}

function questLink(q?: any): string {
  const id = questId(q);
  return id ? `/quests/${encodeURIComponent(id)}` : "/quests";
}

const HomePage: React.FC = () => {
  const { user } = useSession();
  const { t } = useI18n();
  const [home, setHome] = useState<QuestHomeSummary | null>(null);
  const [loadingHome, setLoadingHome] = useState(true);
  const [homeError, setHomeError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoadingHome(true);
      setHomeError(null);
      try {
        const res = await getQuestHome();
        if (!alive) return;
        setHome(res);
      } catch (err: any) {
        if (!alive) return;
        setHomeError(err?.data?.error || err?.message || "Could not load quest home.");
      } finally {
        if (alive) setLoadingHome(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const activeRun = home?.activeRun || null;
  const pendingProposal = home?.pendingProposal || null;
  const recommendedQuest = home?.recommendedQuest || null;
  const dailyObjective = home?.dailyObjective || null;
  const partnerLinked = isPartnerLinked(home?.partner);

  const primaryCta = useMemo(() => {
    if (activeRun) {
      return {
        to: runLink(activeRun),
        label: "Resume quest",
        title: questTitle(activeRun.quest),
        body: `Current status: ${activeRun.status || "active"}`,
      };
    }

    if (pendingProposal) {
      return {
        to: runLink(pendingProposal),
        label: "Review proposal",
        title: questTitle(pendingProposal.quest),
        body: "Your partner is waiting for a response.",
      };
    }

    if (recommendedQuest) {
      return {
        to: questLink(recommendedQuest),
        label: "Start something",
        title: questTitle(recommendedQuest),
        body: questSummary(recommendedQuest, partnerLinked),
      };
    }

    return {
      to: "/quests",
      label: "Open quests",
      title: "Browse quests",
      body: "Pick something that fits your mood.",
    };
  }, [activeRun, pendingProposal, recommendedQuest, partnerLinked]);

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-3xl border border-slate-800 bg-gradient-to-r from-pink-500/10 via-purple-500/10 to-slate-950 p-6 shadow-[0_18px_55px_rgba(0,0,0,0.35)]">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-pink-300">
              {t("home.welcomeHeadline")}
            </div>
            <h1 className="mt-2 text-2xl font-extrabold text-slate-50">
              {home?.user?.displayName || user?.displayName || user?.email || "IntimSync couple"}
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300">
              {t("home.welcomeSub")}
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Link
              to={primaryCta.to}
              className="rounded-full bg-pink-500 px-4 py-2 text-sm font-semibold text-white hover:bg-pink-400"
            >
              {primaryCta.label}
            </Link>
            <Link
              to="/quest-log-history"
              className="rounded-full border border-slate-700 bg-slate-900/80 px-4 py-2 text-sm font-semibold text-slate-200 hover:border-slate-500"
            >
              Quest history
            </Link>
          </div>
        </div>
      </section>

      {homeError && (
        <section className="rounded-3xl border border-amber-500/30 bg-amber-500/10 p-5 text-sm text-amber-100">
          {homeError}
        </section>
      )}

      <section className="grid gap-4 xl:grid-cols-3">
        <QuickCard
          to={primaryCta.to}
          title={loadingHome ? "Loading quest home…" : primaryCta.title}
          body={loadingHome ? "Checking active runs, proposals, and recommendations." : primaryCta.body}
          badge={activeRun ? "active" : pendingProposal ? "review" : "start"}
        />
        <QuickCard
          to={objectiveLink(dailyObjective, activeRun, pendingProposal)}
          title={objectiveTitle(dailyObjective) || "Daily objective"}
          body={objectiveBody(dailyObjective, activeRun, pendingProposal)}
          badge="daily"
        />
        <QuickCard
          to="/quests"
          title="Quest catalog"
          body="Browse private prompts and guided ideas that match your current discovery settings."
          badge="discover"
        />
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-3xl border border-slate-800 bg-slate-950/70 p-5">
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">
            Recommended
          </div>
          <h2 className="mt-2 text-lg font-bold text-slate-50">
            {recommendedQuest ? questTitle(recommendedQuest) : "Start something"}
          </h2>
          <p className="mt-3 text-sm leading-6 text-slate-300">
            {recommendedQuest
                ? questSummary(recommendedQuest, partnerLinked)
                : "Open the quest catalog and choose something that fits your current mood."}
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            <Link
              to={recommendedQuest ? questLink(recommendedQuest) : "/quests"}
              className="rounded-full border border-fuchsia-400/30 bg-fuchsia-500/10 px-3 py-1.5 text-xs font-semibold text-fuchsia-200 hover:border-fuchsia-300/60"
            >
              {recommendedQuest ? "Open recommendation" : "Browse quests"}
            </Link>
          </div>
        </div>

        <div className="rounded-3xl border border-slate-800 bg-slate-950/70 p-5">
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">
            Discovery settings
          </div>
          <h2 className="mt-2 text-lg font-bold text-slate-50">
            Personalized suggestions
          </h2>
          <p className="mt-3 text-sm leading-6 text-slate-300">
            {discoverySettingsBody(partnerLinked)}
          </p>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
